import * as React from 'react';
import { Card } from '@readcloud/ui/card';
import { Label } from '@readcloud/ui/label';
import {
  Carousel,
  CarouselItem,
  CarouselContent,
  CarouselPrevious,
  CarouselNext,
} from '@readcloud/ui/carousel';
import type { CarouselApi } from '@readcloud/ui/carousel';
import cn from 'classnames';

interface UploadBookThumbnailProps {
  fileData: { type: string; name: string };
  pdfDocument: any;
  totalPages: number;
  thumbnailUrl: string | null;
  onThumbnailSelect: (data: string) => void;
}

const UploadBookThumbnail = ({
  fileData,
  pdfDocument,
  totalPages,
  thumbnailUrl,
  onThumbnailSelect,
}: UploadBookThumbnailProps) => {
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [thumbnailsMap, setThumbnailsMap] = React.useState<Map<number, string>>(
    new Map()
  );
  const [api, setApi] = React.useState<CarouselApi | null>(null);

  // Function to determine pages to render
  const getPagesToRender = (
    currentPage: number,
    totalPages: number
  ): number[] => {
    return [
      currentPage - 4,
      currentPage - 3,
      currentPage - 2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      currentPage + 2,
      currentPage + 3,
      currentPage + 4,
    ].filter((page) => page > 0 && page <= totalPages);
  };

  // Function to render a range of pages as thumbnails
  const getPagesAsThumbnails = async (currentPage: number): Promise<void> => {
    if (!pdfDocument) return;

    const pagesToRender = getPagesToRender(currentPage, pdfDocument.numPages);

    await Promise.allSettled(
      pagesToRender.map(async (page) => {
        if (!thumbnailsMap.has(page)) {
          const pdfPage = await pdfDocument.getPage(page);
          const viewport = pdfPage.getViewport({ scale: 1 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          if (context) {
            await pdfPage.render({ canvasContext: context, viewport }).promise;
            const thumbnailDataUrl = canvas.toDataURL();
            setThumbnailsMap((prev) =>
              new Map(prev).set(page, thumbnailDataUrl)
            );
          } else {
            throw new Error('Failed to get canvas context');
          }
        }
      })
    );
  };

  React.useEffect(() => {
    if (pdfDocument) {
      getPagesAsThumbnails(currentPage);
    }
  }, [currentPage, pdfDocument]);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    api.on('select', () => {
      setCurrentPage(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  // Call onThumbnailSelect when a page is rendered or epub thumbnail is ready
  React.useEffect(() => {
    if (thumbnailUrl) {
      onThumbnailSelect(thumbnailUrl);
    }
  }, [thumbnailUrl, onThumbnailSelect]);

  React.useEffect(() => {
    const thumbnail = thumbnailsMap.get(currentPage);
    if (thumbnail) {
      onThumbnailSelect(thumbnail);
    }
  }, [thumbnailsMap, currentPage, onThumbnailSelect]);

  return (
    <>
      <div className="rc-flex rc-flex-col rc-items-center rc-w-full">
        {fileData?.type === 'application/pdf' && (
          <>
            <div className="rc-flex rc-justify-start rc-w-full">
              <Label>Select a Page</Label>
            </div>
            <Carousel
              className="rc-w-full rc-max-w-[10rem]  sm:rc-max-w-[14rem] md:rc-max-w-xs"
              opts={{
                align: 'center',
              }}
              setApi={setApi}
            >
              <CarouselContent className="rc-ml-4 rc-py-6">
                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  const thumbnail = thumbnailsMap.get(page);

                  return (
                    <CarouselItem
                      key={page}
                      className={cn(
                        'rc-pl-4 rc-basis-48',
                        page === currentPage && 'rc-scale-110 rc-transition-all'
                      )}
                    >
                      <Card
                        className={cn(
                          'rc-overflow-hidden rc-w-[7.5rem] md:rc-w-44',
                          page === currentPage &&
                            'rc-border-4 rc-border-primary'
                        )}
                      >
                        {thumbnail ? (
                          <img
                            src={thumbnail}
                            alt={`PDF Thumbnail Page ${page}`}
                            className="rc-object-fill"
                          />
                        ) : (
                            <div className="rc-w-[7.5rem] md:rc-w-44 rc-h-44 md:rc-h-56 rc-flex rc-items-center rc-justify-center rc-bg-gray-200">
                            <span>Loading...</span>
                          </div>
                        )}
                      </Card>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </>
        )}
        {thumbnailUrl && (
          <>
            <div className="rc-flex rc-justify-start rc-w-full">
              <Label>Source Image</Label>
            </div>
            <Card className="rc-overflow-hidden rc-w-44 rc-h-[234.47px]">
              <img
                src={thumbnailUrl}
                alt="Source Image"
                className="rc-w-44 rc-h-[234.47px] rc-object-cover"
              />
            </Card>
          </>
        )}
      </div>
    </>
  );
};

export default UploadBookThumbnail;
